import { useState, useRef, useEffect } from 'react';
import { Copy, Check, ChevronDown, Play } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism-async-light';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('bash', bash);

import { breakpoints } from '@/layouts/breakpoints'
import ENDPOINTS from './InteractiveAPIData'
import { useNavigate } from 'react-router-dom';

const LANG_MAP = {
  JavaScript: 'javascript',
  Python: 'python',
  cURL: 'bash',
};


/* ─── Animations ─── */
const fadeSlide = keyframes`
  from { opacity: 0; transform: translateY(-4px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0)   scale(1); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40%           { transform: scale(1);   opacity: 1;   }
`;

/* ─── Dropdown primitives ─── */
const DropdownWrapper = styled.div`
  position: relative;
  flex: ${p => p.$flex || 'unset'};
  min-width: 0;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  background: #fff;
  border: 1px solid ${p => p.$open ? 'rgba(37,99,235,0.5)' : 'rgba(0,0,0,0.08)'};
  border-radius: 8px;
  padding: 5px 10px 5px 12px;
  font-size: 11.5px;
  color: #374151;
  cursor: pointer;
  outline: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  box-shadow: ${p => p.$open ? '0 0 0 2px rgba(37,99,235,0.12)' : 'none'};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${p => p.$open ? 'rgba(37,99,235,0.5)' : 'rgba(0,0,0,0.16)'};
  }

  svg {
    flex-shrink: 0;
    margin-left: auto;
    color: #9ca3af;
    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform: rotate(${p => p.$open ? '180deg' : '0deg'});
  }
`;

const TriggerText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  min-width: 100%;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.09);
  border-radius: 10px;
  box-shadow:
    0 1px 3px rgba(0,0,0,0.06),
    0 8px 24px rgba(0,0,0,0.1),
    0 20px 48px rgba(0,0,0,0.06);
  z-index: 999;
  overflow: hidden;
  padding: 4px;
  animation: ${fadeSlide} 0.18s cubic-bezier(0.34, 1.2, 0.64, 1) both;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 10px;
  font-size: 11.5px;
  color: ${p => p.$active ? '#2563eb' : '#374151'};
  background: ${p => p.$active ? 'rgba(37,99,235,0.06)' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  font-weight: ${p => p.$active ? '600' : '400'};
  transition: background 0.1s, color 0.1s;
  gap: 8px;

  &:hover {
    background: ${p => p.$active ? 'rgba(37,99,235,0.08)' : 'rgba(0,0,0,0.04)'};
  }
`;

const ActiveDot = styled.span`
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #2563eb;
  flex-shrink: 0;
  opacity: ${p => p.$visible ? 1 : 0};
`;

/* ─── Reusable Dropdown component ─── */
function Dropdown({ options, value, onChange, renderLabel, flex }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <DropdownWrapper ref={ref} $flex={flex}>
      <Trigger $open={open} onClick={() => setOpen(o => !o)} type="button">
        <TriggerText>{renderLabel ? renderLabel(selected) : selected?.label}</TriggerText>
        <ChevronDown size={11} />
      </Trigger>
      {open && (
        <Menu>
          {options.map(opt => (
            <MenuItem
              key={opt.value}
              $active={opt.value === value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              type="button"
            >
              <ActiveDot $visible={opt.value === value} />
              {renderLabel ? renderLabel(opt) : opt.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </DropdownWrapper>
  );
}

/* ─── Styled components ─── */
const Shell = styled.div`
  z-index: 9;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow:
    0 1px 3px rgba(0,0,0,0.05),
    0 8px 32px rgba(0,0,0,0.08),
    0 32px 80px rgba(0,0,0,0.05);
  overflow: hidden;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 18px;
  background: #f4f4f1;
  border-bottom: 1px solid rgba(0,0,0,0.07);
`;

const Dots = styled.div`
  display: flex;
  gap: 7px;
  flex-shrink: 0;
  @media (max-width: ${breakpoints.small}) {
    display: none;
  }
  span {
    display: block;
    width: 12px; height: 12px;
    border-radius: 50%;
    &:nth-child(1) { background: #eee; box-shadow: inset 0 0 0 0.5px rgba(0,0,0,0.15); }
    &:nth-child(2) { background: #eee; box-shadow: inset 0 0 0 0.5px rgba(0,0,0,0.15); }
    &:nth-child(3) { background: #eee; box-shadow: inset 0 0 0 0.5px rgba(0,0,0,0.15); }
  }
`;

const MethodTag = styled.span`
  background: #dcfce7;
  color: #15803d;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.07em;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const DropdownGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  max-width: 560px;
  margin: 0 auto;
`;

const RunBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #2563eb;
  border: none;
  border-radius: 8px;
  padding: 5px 12px 5px 10px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.04em;
  flex-shrink: 0;
  transition: background 0.15s, opacity 0.15s;
  opacity: ${p => p.$disabled ? 0.6 : 1};
  pointer-events: ${p => p.$disabled ? 'none' : 'auto'};

  &:hover { background: #1d4ed8; }
  &:active { background: #1e40af; }
`;

const PanelRow = styled.div`
  display: grid;

  min-height: 400px;
  max-height: 560px;

   grid-template-columns: 1fr 1fr;
   @media (max-width: ${breakpoints.medium}) {
       grid-template-columns: unset;
       grid-template-rows: 250px 1fr;
   }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  &:first-child {
    border-right: 1px solid rgba(0,0,0,0.1);
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 16px;
  background: #f9f9f7;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  flex-shrink: 0;
`;

const Label = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #9ca3af;
  background: rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.07);
  border-radius: 4px;
  padding: 2px 7px;
`;

const StatusTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #15803d;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
  padding: 2px 7px;
`;

const BtnRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid rgba(0,0,0,0.09);
  border-radius: 8px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.03em;
  transition: all 0.15s;

  &:hover {
    background: rgba(0,0,0,0.03);
    border-color: rgba(0,0,0,0.16);
    color: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
  }
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6px;
  padding-top: 10px;
  background: #f9f9f7;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  flex-shrink: 0;
  .cpb {
    margin-top: -10px;
    margin-right: 12px;
  }
`;

const Tab = styled.button`
  position: relative;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 9px 12px 8px;
  font-size: 11.5px;
  font-weight: ${p => p.$active ? '700' : '500'};
  color: #6b7280;
  opacity: ${p => p.$active ? '1' : '0.7'};
  letter-spacing: 0.02em;
  transition-duration: 0.2s;

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 8px; right: 8px;
    height: 2px;
    background: #60a5fa;
    border-radius: 2px 2px 0 0;
    opacity: ${p => p.$active ? 1 : 0};
    transform: scaleX(${p => p.$active ? 1 : 0});
    transform-origin: left;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.15s;
  }

  &:hover {
    opacity: 1;
  }
`;

const CodeWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 5px;
`;

const Dot = styled.span`
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #60a5fa;
  animation: ${dotPulse} 1.2s ease-in-out infinite;
  animation-delay: ${p => p.$i * 0.2}s;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 13px;
  background: #2563eb;
  margin-left: 1px;
  vertical-align: text-bottom;
  border-radius: 1px;
  position: absolute;
  bottom: 24px;
  left: 20px;
  animation: ${blink} 0.8s step-start infinite;
`;

const highlighterStyle = {
  margin: 0,
  padding: '20px 20px 60px',
  fontSize: '12px',
  lineHeight: '1.8',
  flex: 1,
  overflow: 'auto',
  height: '500px',
  minHeight: '500px',
  maxHeight: '500px',
  borderRadius: 0,
};

/* ─── Main component ─── */
const InteractiveAPI = () => {
  const navigate = useNavigate()
  const [activeLang,        setActiveLang]        = useState('JavaScript');
  const [activeEndpoint,    setActiveEndpoint]    = useState(ENDPOINTS[0]);
  const [copiedCode,        setCopiedCode]        = useState(false);
  const [copiedJSON,        setCopiedJSON]        = useState(false);

  // Committed state - only updates on Run
  const [displayedEndpoint, setDisplayedEndpoint] = useState(ENDPOINTS[0]);

  // Response animation state
  const [typedResponse, setTypedResponse] = useState(() => ENDPOINTS[0].response);
  const [isLoading,     setIsLoading]     = useState(false);
  const [isTyping,      setIsTyping]      = useState(false);

  const typingRef = useRef(null);
  const loadingTimerRef = useRef(null);

  useEffect(() => () => {
    if (typingRef.current) clearInterval(typingRef.current);
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
  }, []);


  const copy = async (text, set) => {
    await navigator.clipboard.writeText(text);
    set(true);
    setTimeout(() => set(false), 2000);
  };

  const runEndpoint = (endpoint) => {
    if (typingRef.current) clearInterval(typingRef.current);
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);  // ← new

    setIsLoading(true);
    setIsTyping(false);
    setTypedResponse('');

    loadingTimerRef.current = setTimeout(() => {   // ← tracked now
      const newResponse = endpoint.response;
      setDisplayedEndpoint(endpoint);
      setIsLoading(false);
      setIsTyping(true);

      let i = 0;
      const lines = newResponse.split('\n');
      typingRef.current = setInterval(() => {
        i += 1;
        setTypedResponse(lines.slice(0, i).join('\n'));
        if (i >= lines.length) {
          setTypedResponse(newResponse);
          clearInterval(typingRef.current);
          setIsTyping(false);
        }
      }, 10);
    }, 250);
  };

  // Auto-run whenever the active endpoint changes
  useEffect(() => {
    runEndpoint(activeEndpoint);
  }, [activeEndpoint]);

  const handleRun = () => {
    if (isLoading || isTyping) return;
    runEndpoint(activeEndpoint);
  };

  const code = activeEndpoint.code[activeLang];

  const endpointOptions = ENDPOINTS.map(ep => ({
    value: ep.id,
    label: ep.url,
  }));


  return (
    <Shell>
      <TitleBar>
        <Dots><span /><span /><span /></Dots>
        <DropdownGroup>
          <MethodTag>{activeEndpoint.method}</MethodTag>

          <Dropdown
            flex="1"
            options={endpointOptions}
            value={activeEndpoint.id}
            onChange={(id) => { if(id === 'seeAll') {navigate('/docs')} else {setActiveEndpoint(ENDPOINTS.find(ep => ep.id === id)) } } }
          />
        </DropdownGroup>

        <RunBtn $disabled={isLoading || isTyping} onClick={handleRun}>
          <Play size={10} fill="#fff" />
          Run
        </RunBtn>
      </TitleBar>

      <PanelRow>
        {/* ── Left: Code ── */}
        <Panel className="right">
          <TabRow>
            <div>
              {Object.keys(LANG_MAP).map(lang => (
                <Tab key={lang} $active={activeLang === lang} onClick={() => setActiveLang(lang)}>
                  {lang}
                </Tab>
              ))}
            </div>

            <CopyBtn
              className="cpb"
              onClick={() => copy(code, setCopiedCode)}
            >
              {copiedCode
                ? <><Check size={11} /> Copied</>
                : <><Copy size={11} /> Copy</>
              }
            </CopyBtn>
          </TabRow>

          <CodeWrapper>
            <SyntaxHighlighter
              language={LANG_MAP[activeLang]}
              style={oneLight}
              customStyle={highlighterStyle}
              key={`${activeEndpoint.id}-${activeLang}`}
            >
              {code}
            </SyntaxHighlighter>
          </CodeWrapper>
        </Panel>

        {/* ── Right: Response ── */}
        <Panel>
          <PanelHeader>
            <Label>Response</Label>
            <BtnRow>
              <StatusTag>200 OK</StatusTag>
              <CopyBtn
                onClick={() => copy(typedResponse, setCopiedJSON)}
                disabled={isLoading || isTyping}
              >
                {copiedJSON
                  ? <><Check size={11} /> Copied</>
                  : <><Copy size={11} /> Copy</>
                }
              </CopyBtn>
            </BtnRow>
          </PanelHeader>

          {isLoading ? (
            <LoadingWrap>
              <Dot $i={0} /><Dot $i={1} /><Dot $i={2} />
            </LoadingWrap>
          ) : (
            <SyntaxHighlighter
              language="json"
              style={oneLight}
              customStyle={{ ...highlighterStyle }}
              key={`response-${displayedEndpoint.id}`}
            >
              {typedResponse}
            </SyntaxHighlighter>
          )}
          {isTyping && !isLoading && <Cursor />}
        </Panel>
      </PanelRow>
    </Shell>
  );
};

export default InteractiveAPI;
